import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setUser, User } from '@/Redux/slices/authSlice';
import { getDynatraceLogger } from '@/Services';
import { listenToParentMessages, notifyIframeReady } from '@/Utils/iframe-utils';

interface AuthMessage {
  type: 'INIT_AUTH' | 'AUTH_REFRESH';
  payload: {
    user: {
      access_token: string;
      token_type: string;
      expires_at: number;  // Unix timestamp in seconds
      expired: boolean;
      refresh_token?: string;
    };
    timestamp: number;
  };
}

/**
 * PostMessage-based authentication initializer
 * Replaces the OIDC-based AuthInitializer
 * 
 * Flow:
 * 1. On mount, send IFRAME_READY to parent
 * 2. Wait for INIT_AUTH message from parent
 * 3. Store user in Redux and continue
 * 4. Listen for AUTH_REFRESH to update token seamlessly
 */
export const PostMessageAuthInitializer = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Notify parent that iframe is ready and waiting for auth
    notifyIframeReady();

    // Listen for authentication messages from parent
    const cleanup = listenToParentMessages((message) => {
      if (message.type === 'INIT_AUTH' || message.type === 'AUTH_REFRESH') {
        const authMessage = message as AuthMessage;
        const receivedUser = authMessage.payload.user;

        // Validate token
        if (!receivedUser || !receivedUser.access_token) {
          const logger = getDynatraceLogger();
          logger?.logError('Invalid auth data received from parent', undefined, {
            hasUser: !!receivedUser,
            hasToken: !!receivedUser?.access_token,
          });
          return;
        }

        // Get clientId from URL params since it's not in the user object from Premium
        const urlParams = new URLSearchParams(window.location.search);
        const urlClientId = urlParams.get('clientId') || urlParams.get('punterId') || 'unknown';

        // Construct full User object with profile
        const fullUser: User = {
          access_token: receivedUser.access_token,
          token_type: receivedUser.token_type,
          expires_at: receivedUser.expires_at,
          expired: receivedUser.expired,
          profile: {
            sub: urlClientId,  // Use clientId from URL
          },
          refresh_token: receivedUser.refresh_token,
        };

        // Store user in Redux
        dispatch(setUser(fullUser));
      }
    });

    return cleanup;
  }, [dispatch]);

  return null;
};
